import React, {useContext, useEffect, useState} from 'react'
import {ReducerContext} from './reducer'
import {Table, Alert, Switch} from 'antd'
import NoData from './NoData'
import DB from '../DAL/DB'

export default function SOTable() {
  const {state, dispatch} = useContext(ReducerContext)
  const [useTransformLogic, setUseTransformLogic] = useState(false)
  
  // Determine aggregator type and value
  const aggType = state.dropdown.aggregation === 'reviewer' 
    ? 'reviewer' 
    : state.dropdown.aggregation === 'installer' 
      ? 'installer'
      : 'field_user';
      
  const selectedUser = aggType === 'reviewer' 
    ? state.dropdown.reviewer 
    : aggType === 'installer' 
      ? state.dropdown.installerUser
      : state.dropdown.fieldUser;

  useEffect(() => {
    const loadSOData = async () => {
      if (state.dropdown.rubric) {
        const aggregations = new DB.Aggregations()
        
        const {ok, data} = await aggregations.get(null, {
          filters: [
            [`rubric`, `==`, state.dropdown.rubric],
            [`aggregation_type`, `==`, aggType],
          ],
        })
        
        if (ok && data && data.length > 0) {
          dispatch({type: `SO_data`, value: data[0]})
        }
      }
    }
    
    loadSOData()
  }, [state.dropdown.rubric, aggType, state.dropdown.fieldUser, state.dropdown.reviewer, state.dropdown.installerUser])

  //Define function that renders cells based on pvalue
  const renderCell = (pvalue) => {
    let cellStyle = {borderRadius: '15px', textAlign: 'center'}
    if (typeof pvalue !== 'number' || isNaN(pvalue)) {
      return <div style={{backgroundColor: 'darkGray', ...cellStyle}}>No Data</div>
    }
    pvalue = pvalue.toFixed(6)
    // Reverse the color coding to match the Summary View logic:
    // Red for positive values (worse than average), Green for negative values (better than average)
    if (pvalue < 0 && pvalue > -0.15) {
      return <div style={{backgroundColor: 'lightGreen', ...cellStyle}}>{pvalue}</div>
    } else if (pvalue > 0 && pvalue < 0.15) {
      return <div style={{backgroundColor: 'lightCoral', ...cellStyle}}>{pvalue}</div>
    } else {
      return <div style={{backgroundColor: 'lightGray', ...cellStyle}}>{pvalue}</div>
    }
  }

  // New render function for transform logic (simple binary better/worse with significance check)
  const renderCellTransform = (value) => {
    let cellStyle = {borderRadius: '15px', textAlign: 'center'}
    if (typeof value !== 'object' || !value) {
      return <div style={{backgroundColor: 'darkGray', ...cellStyle}}>No Data</div>
    }
    
    const { pvalue, comparison } = value;
    
    // Check if p-value is significant (< 0.15)
    if (Math.abs(pvalue) >= 0.15) {
      return <div style={{backgroundColor: 'lightGray', ...cellStyle}}>N/A</div>
    }
    
    // For significant p-values, show better/worse based on performance comparison
    if (comparison > 0) {
      return <div style={{backgroundColor: 'lightGreen', ...cellStyle}}>Better</div>
    } else if (comparison < 0) {
      return <div style={{backgroundColor: 'lightCoral', ...cellStyle}}>Worse</div>
    } else {
      return <div style={{backgroundColor: 'lightGray', ...cellStyle}}>Equal</div>
    }
  }

  let rows = []
  let people = []
  let columns

  //Columns should be each reviewer, or field_user, etc... by name
  if (state.SO_data) {
    let agg_type = aggType

    // Filter questions for selected user if not "All" or "select"
    let questionsToShow = state.SO_data.questions;
    if (selectedUser && selectedUser !== 'All' && selectedUser !== 'select') {
      questionsToShow = state.SO_data.questions
        .map((question) => {
          // Filter results for the selected user
          const userResults = question.results.filter(result => result[agg_type] === selectedUser);
          if (userResults.length > 0) {
            return {
              ...question,
              results: userResults
            };
          }
          return null;
        })
        .filter(Boolean);
    }

    questionsToShow.forEach((question) => {
      // Create one JSON object per question
      let row = question

      question.results.forEach((result) => {
        if (useTransformLogic) {
          // Use transformData logic with significance check
          const comparison = result.avg > question.global_avg ? 1 : result.avg < question.global_avg ? -1 : 0;
          question[result[agg_type]] = {
            pvalue: result.pvalue,
            comparison: comparison
          };
        } else {
          // Current implementation: p-value with sign based on performance
          question[result[agg_type]] = result.pvalue * Math.sign(question.global_avg - result.avg)
        }
      })

      rows.push(row)

      //QuestionPeople is the list of all people that answered this question
      let questionPeople = question.results.map((result) => {
        return {
          title: result[agg_type],
          key: result[agg_type],
          dataIndex: result[agg_type],
          render: useTransformLogic ? renderCellTransform : renderCell,
        }
      })

      //Reduce to deduplicated array
      let oldPeople = people.map((person) => person.key)
      let newPeople = []

      questionPeople.forEach((person) => {
        if (!oldPeople.includes(person.key)) {
          newPeople.push(person)
        }
      })

      // Add new to old
      people = people.concat(newPeople)
    })

    //If there was an error it's possible that database docs get written with "undefined" in each field so filter those out.
    people = people.filter((person) => person.key)

    //Append name to resource for the person
    if (people && people.length > 0) {
      columns = [
        {title: 'Question', key: 'question', dataIndex: 'question'},
        ...people.map(person => {
          let title = person.title;
          if (state.allUsers && state.allUsers[person.key] && state.allUsers[person.key].name) {
            title = state.allUsers[person.key].name;
          }
          return {
            ...person,
            title
          };
        })
      ];
    } else {
      people = null
    }
  }

  let returnDiv = <NoData />

  if (people) {
    returnDiv = (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Switch 
            checked={useTransformLogic}
            onChange={setUseTransformLogic}
            checkedChildren="Transform Logic"
            unCheckedChildren="P-Value Logic"
          />
          <span>
            {useTransformLogic 
              ? "Using Tables.js logic (simple better/worse comparison)" 
              : "Using current p-value calculation"
            }
          </span>
        </div>
        {!useTransformLogic}
        <Table dataSource={rows} columns={columns} scroll={{ x: 'max-content' }}></Table>
      </div>
    )
  }

  return returnDiv
}
